import { useTranslation } from 'react-i18next'
import { Award, CheckCircle, FileCheck, Globe } from 'lucide-react'


export function CertificationsSection() {
  const { t } = useTranslation('common')

  const certifications = [
    {
      icon: Award,
      key: 'brcgs',
    },
    {
      icon: FileCheck,
      key: 'ifs',
    },
    {
      icon: CheckCircle,
      key: 'quality',
    },
    {
      icon: Globe,
      key: 'sustainability',
    },
  ]

  const standards = [
    'hygiene',
    'traceability',
    'controls',
    'training',
  ]

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-[#00441C]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#DF631E]/5 rounded-full blur-3xl" />

      <div className="container-fiorucci relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-[#00441C]/10 text-[#00441C] text-sm font-medium rounded-full mb-4">
            {t('nutrition.certifications.badge')}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#4E2118] mb-6">
            {t('nutrition.certifications.title')}
          </h2>
          <p className="text-lg text-[#4E2118]/70 leading-relaxed">
            {t('nutrition.certifications.subtitle')}
          </p>
        </div>

        {/* Main Certifications Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {certifications.map((cert) => (
            <div
              key={cert.key}
              className="group p-6 bg-[#FDF8F3] rounded-2xl border border-[#4E2118]/5 hover:border-[#DF631E]/20 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center mb-4 shadow-sm group-hover:scale-110 transition-transform duration-300">
                <cert.icon className="w-7 h-7 text-[#DF631E]" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-[#4E2118] mb-2">
                {t(`nutrition.certifications.items.${cert.key}.title`)}
              </h3>
              <p className="text-sm text-[#4E2118]/70 leading-relaxed">
                {t(`nutrition.certifications.items.${cert.key}.description`)}
              </p>
            </div>
          ))}
        </div>

        {/* BRCGS Highlight Box */}
        <div className="bg-gradient-to-br from-[#4E2118] to-[#3A1912] rounded-2xl p-8 md:p-12 text-white mb-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full mb-4">
                <Award className="w-4 h-4 text-[#DF631E]" />
                <span className="text-sm font-medium text-white/90">{t('nutrition.certifications.brcgs.badge')}</span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold mb-4">
                {t('nutrition.certifications.brcgs.title')}
              </h3>
              <p className="text-white/80 leading-relaxed mb-6">
                {t('nutrition.certifications.brcgs.description')}
              </p>
              <ul className="space-y-2">
                {['global', 'rigorous', 'recognized'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#DF631E] flex-shrink-0" />
                    <span className="text-white/90">{t(`nutrition.certifications.brcgs.points.${item}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                {/* Certificate visual representation */}
                <div className="w-48 h-60 bg-white rounded-lg shadow-2xl flex flex-col items-center justify-center p-6 text-center transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  <div className="w-16 h-16 rounded-full bg-[#DF631E]/10 flex items-center justify-center mb-4">
                    <Award className="w-8 h-8 text-[#DF631E]" />
                  </div>
                  <span className="text-xs font-bold text-[#4E2118]/40 uppercase tracking-wider mb-2">Certified</span>
                  <span className="font-serif text-lg font-bold text-[#4E2118] leading-tight">BRCGS</span>
                  <span className="text-xs text-[#4E2118]/60 mt-1">Food Safety</span>
                  <div className="mt-4 pt-4 border-t border-[#4E2118]/10 w-full">
                    <span className="text-xs text-[#00441C] font-medium">Issue 9</span>
                  </div>
                </div>
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-[#DF631E] rounded-full opacity-20" />
                <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-[#00441C] rounded-full opacity-20" />
              </div>
            </div>
          </div>
        </div>

        {/* Quality Standards List */}
        <div className="bg-[#FDF8F3] rounded-2xl p-8">
          <h3 className="font-serif text-xl font-semibold text-[#4E2118] mb-6 text-center">
            {t('nutrition.certifications.standardsTitle')}
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {standards.map((standard) => (
              <div
                key={standard}
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-[#4E2118]/5"
              >
                <div className="w-10 h-10 rounded-full bg-[#00441C]/10 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-[#00441C]" />
                </div>
                <span className="font-medium text-[#4E2118] text-sm">
                  {t(`nutrition.certifications.standards.${standard}`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
